import type { AnnualLocationData, AnnualYearData } from '@/data/annual-calendar/types';
import { GOOD_NIGHT_MIN, VISIBILITY_BANDS, isGoodNight, type BandName } from '@/utils/visibilityBands';

/**
 * A plain-language reading of one year's existing Saturday grid.
 * Every date and month in the result is a label already stored on that grid.
 * There is no second astronomy pass and no hand-written season file.
 */
export interface LocationSeasonLine {
  name: string;
  firstSaturday: string | null;
  lastSaturday: string | null;
  peakMonths: string[];
  text: string;
}

export interface BestSaturdayLine {
  label: string;
  goodSites: number;
  topRating: number;
  text: string;
}

export interface CalendarSeasonSummary {
  hasRatings: boolean;
  readingGuide: string;
  locations: LocationSeasonLine[];
  bestHeading: string;
  bestIntro: string;
  bestSaturdays: BestSaturdayLine[];
  faqQuestion: string;
  faqAnswer: string;
}

const MONTH_NAME: Record<string, string> = {
  Jan: 'January',
  Feb: 'February',
  Mar: 'March',
  Apr: 'April',
  May: 'May',
  Jun: 'June',
  Jul: 'July',
  Aug: 'August',
  Sep: 'September',
  Oct: 'October',
  Nov: 'November',
  Dec: 'December',
};

/** Named rows to quote when the grid has them. Otherwise the nearest latitude. */
const NORTHERN_NAMES = ['Moab', 'Denver'];
const SOUTHERN_NAMES = ['Atacama Desert'];
const NORTH_LATITUDE = 38;
const SOUTH_LATITUDE = -24;
const BEST_LIMIT = 5;

export const SEASON_READING_GUIDE =
  'A green cell is a Saturday rated Good or better: the galactic core is well placed and the moon is not washing it out. Read across a location\'s row. The run of green is the season at that site, and it is not the same row everywhere.';

function joinAnd(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

function closest(pool: AnnualLocationData[], target: number): AnnualLocationData | undefined {
  return pool
    .slice()
    .sort((a, b) => Math.abs(a.latitude - target) - Math.abs(b.latitude - target))[0];
}

function pickLocations(locations: AnnualLocationData[]): AnnualLocationData[] {
  const byName = new Map(locations.map((location) => [location.name, location]));
  const picked: AnnualLocationData[] = [];

  for (const name of NORTHERN_NAMES) {
    const location = byName.get(name);
    if (location && location.latitude > 0) picked.push(location);
  }
  if (picked.length === 0) {
    const north = closest(locations.filter((location) => location.latitude > 0), NORTH_LATITUDE);
    if (north) picked.push(north);
  }

  const namedSouth = SOUTHERN_NAMES.map((name) => byName.get(name)).find(
    (location): location is AnnualLocationData => !!location && location.latitude < 0,
  );
  const south = namedSouth ?? closest(locations.filter((location) => location.latitude < 0), SOUTH_LATITUDE);
  if (south && !picked.includes(south)) picked.push(south);

  return picked;
}

function monthToken(label: string): string {
  return label.split(' ')[0] ?? '';
}

function peakMonthNames(saturdays: string[], indexes: number[]): string[] {
  const counts = new Map<string, number>();
  const order: string[] = [];
  for (const index of indexes) {
    const token = monthToken(saturdays[index] ?? '');
    if (!token) continue;
    if (!counts.has(token)) order.push(token);
    counts.set(token, (counts.get(token) ?? 0) + 1);
  }
  const max = Math.max(0, ...counts.values());
  if (max === 0) return [];
  return order.filter((token) => counts.get(token) === max).map((token) => MONTH_NAME[token] ?? token);
}

/** Strongest band that actually appears on the row. Excellent first, then Very Good, then Good. */
const PEAK_BANDS: readonly BandName[] = ['Excellent', 'Very Good', 'Good'];

function bandMin(name: BandName): number {
  return VISIBILITY_BANDS.find((band) => band.name === name)?.min ?? GOOD_NIGHT_MIN;
}

function peakOnRow(
  saturdays: string[],
  cells: Array<{ rating: number }>,
): { band: BandName; months: string[] } | null {
  for (const band of PEAK_BANDS) {
    const min = bandMin(band);
    const indexes = cells.flatMap((cell, index) => (
      index < saturdays.length && cell.rating >= min ? [index] : []
    ));
    if (indexes.length === 0) continue;
    return { band, months: peakMonthNames(saturdays, indexes) };
  }
  return null;
}

function locationText(
  name: string,
  first: string | null,
  last: string | null,
  peak: { band: BandName; months: string[] } | null,
): string {
  if (!first || !last) return `At ${name}, no Saturday on this grid is rated Good or better.`;
  if (first === last) return `At ${name}, the only Saturday rated Good or better is ${first}.`;
  const months = peak?.months ?? [];
  const peakSentence = months.length === 0
    ? ''
    : months.length === 1
      ? `The most ${peak?.band} Saturdays fall in ${months[0]}.`
      : `The most ${peak?.band} Saturdays fall in ${joinAnd(months)}.`;
  return `At ${name}, the first Saturday rated Good or better is ${first} and the last is ${last}. ${peakSentence}`.trim();
}

function summarizeLocation(location: AnnualLocationData, saturdays: string[]): LocationSeasonLine {
  const indexes: number[] = [];
  location.cells.forEach((cell, index) => {
    if (index < saturdays.length && isGoodNight(cell.rating)) indexes.push(index);
  });
  const first = indexes.length > 0 ? saturdays[indexes[0]] : null;
  const last = indexes.length > 0 ? saturdays[indexes[indexes.length - 1]] : null;
  const peak = peakOnRow(saturdays, location.cells);
  return {
    name: location.name,
    firstSaturday: first,
    lastSaturday: last,
    peakMonths: peak?.months ?? [],
    text: locationText(location.name, first, last, peak),
  };
}

const MONTH_INDEX: Record<string, number> = {
  Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
  Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
};

/**
 * The five labels the month cards have always used. The color belongs to the
 * label. Which card wears it comes from the grid.
 */
export const SEASON_PHASES = [
  'Early Season',
  'Season Opens',
  'Peak Season',
  'Late Season',
  'Off-Season',
] as const;

export type SeasonPhase = (typeof SEASON_PHASES)[number];

/** Class strings from the pre-#244 cards. Do not restyle these. */
export const SEASON_PHASE_STYLE: Record<SeasonPhase, { border: string; badge: string }> = {
  'Early Season': { border: 'border-blue-500', badge: 'text-blue-600 bg-blue-100' },
  'Season Opens': { border: 'border-emerald-600', badge: 'text-emerald-700 bg-emerald-100' },
  'Peak Season': { border: 'border-amber-500', badge: 'text-amber-700 bg-amber-100' },
  'Late Season': { border: 'border-orange-500', badge: 'text-orange-700 bg-orange-100' },
  'Off-Season': { border: 'border-slate-400', badge: 'text-slate-600 bg-slate-200' },
};

export interface MonthGuideRange {
  id: string;
  title: string;
  months: readonly string[];
  chips: readonly string[];
}

/** The five cards, in the original order, with the original titles and chips. */
export const MONTH_GUIDE_RANGES: readonly MonthGuideRange[] = [
  {
    id: 'jan-feb',
    title: 'January – February',
    months: ['Jan', 'Feb'],
    chips: ['Atacama Desert', 'Namib Desert', 'Uluru', 'Canary Islands'],
  },
  {
    id: 'mar-apr',
    title: 'March – April',
    months: ['Mar', 'Apr'],
    chips: ['Moab', 'Death Valley', 'Grand Canyon', 'Dolomites', 'Snowdonia'],
  },
  {
    id: 'may-jul',
    title: 'May – July',
    months: ['May', 'Jun', 'Jul'],
    chips: ['Mauna Kea', 'Tenerife', 'Atacama', 'Namib Desert'],
  },
  {
    id: 'aug-sep',
    title: 'August – September',
    months: ['Aug', 'Sep'],
    chips: ['Jasper', 'Cherry Springs', 'Aoraki Mackenzie', 'Torres del Paine'],
  },
  {
    id: 'oct-dec',
    title: 'October – December',
    months: ['Oct', 'Nov', 'Dec'],
    chips: ['Atacama', 'Aoraki Mackenzie'],
  },
];

export interface MonthGuideClaim {
  sites: string[];
  months: string[];
  band: BandName;
}

export interface MonthGuideCard extends MonthGuideRange {
  /**
   * Border and pill color from the existing palette. The phase name is not
   * shown. The words in the pill are `badge`.
   */
  tone: SeasonPhase;
  /** "Best for Moab & Atacama Desert", or null when nobody's best month is in this card. */
  badge: string | null;
  /** One sentence. Null claim means the card uses its fallback. */
  claim: MonthGuideClaim | null;
  gridSentence: string;
}

interface MonthTally {
  excellent: number;
  veryGood: number;
  good: number;
  saturdays: number;
}

function talliesByMonth(data: AnnualYearData): Map<string, MonthTally> {
  const tallies = new Map<string, MonthTally>();
  data.saturdays.forEach((label, index) => {
    const token = monthToken(label);
    const tally = tallies.get(token) ?? { excellent: 0, veryGood: 0, good: 0, saturdays: 0 };
    tally.saturdays += 1;
    for (const site of data.locations) {
      const rating = site.cells[index]?.rating ?? 0;
      if (rating >= bandMin('Excellent')) tally.excellent += 1;
      else if (rating >= bandMin('Very Good')) tally.veryGood += 1;
      else if (rating >= GOOD_NIGHT_MIN) tally.good += 1;
    }
    tallies.set(token, tally);
  });
  return tallies;
}

function rate(count: number, saturdays: number): number {
  return saturdays > 0 ? count / saturdays : 0;
}

/**
 * Which existing border and pill color this card keeps. The name is not
 * rendered. A one-Saturday tie is not allowed to paint every card amber.
 */
export function monthCardBadge(
  data: AnnualYearData,
  months: readonly string[],
): SeasonPhase {
  const tallies = talliesByMonth(data);
  const inCard = months.map((month) => tallies.get(month)).filter((tally): tally is MonthTally => !!tally);
  const goodCells = inCard.reduce((sum, tally) => sum + tally.excellent + tally.veryGood + tally.good, 0);
  if (goodCells === 0) return 'Off-Season';

  const humps = [...tallies.entries()]
    .filter(([, tally]) => tally.excellent > data.locations.length)
    .map(([token]) => token);

  const cardScore = (cardMonths: readonly string[]) => {
    const rows = cardMonths.map((month) => tallies.get(month)).filter((tally): tally is MonthTally => !!tally);
    const saturdays = rows.reduce((sum, tally) => sum + tally.saturdays, 0);
    return {
      excellent: rate(rows.reduce((sum, tally) => sum + tally.excellent, 0), saturdays),
      veryGood: rate(rows.reduce((sum, tally) => sum + tally.veryGood, 0), saturdays),
      good: rate(rows.reduce((sum, tally) => sum + tally.good, 0), saturdays),
    };
  };
  const better = (a: ReturnType<typeof cardScore>, b: ReturnType<typeof cardScore>) => (
    b.excellent - a.excellent || b.veryGood - a.veryGood || b.good - a.good
  );

  const peakTokens = humps.length > 0 ? humps : winningMonths(cardScore, better);
  if (isPeakCard(months, humps, cardScore, better)) return 'Peak Season';

  const firstPeak = Math.min(...peakTokens.map((token) => MONTH_INDEX[token] ?? 0));
  const cardLast = Math.max(...months.map((month) => MONTH_INDEX[month] ?? 0));
  if (peakTokens.length > 0 && cardLast < firstPeak) {
    const reachesVeryGood = inCard.some((tally) => tally.excellent + tally.veryGood > 0);
    return reachesVeryGood ? 'Season Opens' : 'Early Season';
  }
  return 'Late Season';
}

function winningMonths(
  cardScore: (cardMonths: readonly string[]) => { excellent: number; veryGood: number; good: number },
  better: (
    a: { excellent: number; veryGood: number; good: number },
    b: { excellent: number; veryGood: number; good: number },
  ) => number,
): string[] {
  const ranked = MONTH_GUIDE_RANGES
    .map((range) => ({ months: range.months, score: cardScore(range.months) }))
    .sort((a, b) => better(a.score, b.score));
  const best = ranked[0]?.score;
  if (!best) return [];
  return ranked
    .filter((row) => better(row.score, best) === 0)
    .flatMap((row) => [...row.months]);
}

function isPeakCard(
  months: readonly string[],
  humps: string[],
  cardScore: (cardMonths: readonly string[]) => { excellent: number; veryGood: number; good: number },
  better: (
    a: { excellent: number; veryGood: number; good: number },
    b: { excellent: number; veryGood: number; good: number },
  ) => number,
): boolean {
  if (humps.length > 0) return months.some((month) => humps.includes(month));
  const mine = cardScore(months);
  const best = MONTH_GUIDE_RANGES.map((range) => cardScore(range.months)).sort(better)[0];
  return !!best && better(mine, best) === 0;
}

const GRID_TEMPLATES: Record<string, readonly string[]> = {
  'jan-feb': [
    "On this year's grid, the best run of {band} Saturdays at {sites} comes in {months}.",
    "For {sites}, this year's grid puts the most {band} Saturdays in {months}.",
  ],
  'mar-apr': [
    'Count the {band} Saturdays at {sites} this year, and the biggest share lands in {months}.',
    'The {band} Saturdays at {sites} bunch up in {months} this year.',
    "Mark {months} if you're watching {sites}, since that's where this year's grid has the most Saturdays rated {band}.",
  ],
  'may-jul': [
    "The grid's strongest stretch for {sites} this year lands in {months}, with the most Saturdays rated {band}.",
    'For this year, the longest run of {band} Saturdays at {sites} sits in {months}.',
    'This year, the most {band} Saturdays at {sites} turn up in {months}.',
  ],
  'aug-sep': [
    "Watch {months} for {sites}, since that's when this year's grid has the most Saturdays rated {band}.",
    'Later in the year the grid still has a run of {band} Saturdays at {sites}, and most of them land in {months}.',
  ],
  'oct-dec': [
    'Even this late, the grid shows the most {band} Saturdays at {sites} in {months}.',
    "This year's grid saves its most {band} Saturdays at {sites} for {months}.",
  ],
};

const GRID_FALLBACKS: Record<string, string> = {
  'jan-feb': "None of the locations in this year's grid has its best run of Saturdays in January or February, so check the ratings above before you commit to a trip.",
  'mar-apr': "The locations in this year's grid don't have their best Saturdays in March or April, so use the ratings above to find the nights that still work.",
  'may-jul': 'This year the strongest Saturdays for the locations in the grid fall outside May through July, so read the ratings above night by night.',
  'aug-sep': "No location in this year's grid has its strongest run of Saturdays in August or September, and the ratings above show which nights are still worth the drive.",
  'oct-dec': "None of the locations in this year's grid saves its best Saturdays for October through December.",
};

function fillTemplate(template: string, claim: MonthGuideClaim): string {
  return template
    .replaceAll('{band}', claim.band)
    .replaceAll('{sites}', joinAnd(claim.sites))
    .replaceAll('{months}', joinAnd(claim.months));
}

interface SiteBest {
  name: string;
  tokens: string[];
  excellent: number;
}

const MONTH_TOKENS = Object.keys(MONTH_NAME);

/**
 * The month with the most Excellent Saturdays on this row. A tie breaks
 * toward the month with more Very Good Saturdays. Months that stay tied
 * are all best months. A row with neither is not given a best month.
 */
function bestMonthsForSite(data: AnnualYearData, site: AnnualLocationData): SiteBest | null {
  const counts = new Map<string, { excellent: number; veryGood: number }>();
  data.saturdays.forEach((label, index) => {
    const token = monthToken(label);
    if (!token) return;
    const rating = site.cells[index]?.rating ?? 0;
    const row = counts.get(token) ?? { excellent: 0, veryGood: 0 };
    if (rating >= bandMin('Excellent')) row.excellent += 1;
    else if (rating >= bandMin('Very Good')) row.veryGood += 1;
    counts.set(token, row);
  });
  let excellent = 0;
  for (const row of counts.values()) {
    if (row.excellent > excellent) excellent = row.excellent;
  }
  const tied = MONTH_TOKENS.filter((token) => counts.has(token) && (counts.get(token)?.excellent ?? 0) === excellent);
  let veryGood = 0;
  for (const token of tied) {
    const count = counts.get(token)?.veryGood ?? 0;
    if (count > veryGood) veryGood = count;
  }
  const tokens = tied.filter((token) => (counts.get(token)?.veryGood ?? 0) === veryGood);
  if (excellent === 0 && veryGood === 0) return null;
  return { name: site.name, tokens, excellent };
}

/**
 * One claim per card. The sites are the quoted rows (Moab, Denver, Atacama
 * Desert) whose best month falls in the card. When none of those do, the
 * card names every location that does. Months are only the tied best months
 * inside this card, so April, July, and October are said on three cards.
 */
function claimsForYear(data: AnnualYearData): Array<MonthGuideClaim | null> {
  const best = data.locations
    .map((site) => bestMonthsForSite(data, site))
    .filter((site): site is SiteBest => site !== null);
  const featured = pickLocations(data.locations).map((site) => site.name);
  return MONTH_GUIDE_RANGES.map((range) => {
    const here = best.filter((site) => site.tokens.some((token) => range.months.includes(token)));
    if (here.length === 0) return null;
    const featuredHere = featured.filter((name) => here.some((site) => site.name === name));
    const names = featuredHere.length > 0 ? featuredHere : here.map((site) => site.name);
    const chosen = here.filter((site) => names.includes(site.name));
    const tokens = MONTH_TOKENS.filter((token) => (
      range.months.includes(token) && chosen.some((site) => site.tokens.includes(token))
    ));
    const allExcellent = chosen.every((site) => site.excellent > 0);
    const band: BandName = allExcellent ? 'Excellent' : 'Very Good';
    return {
      sites: names,
      months: tokens.map((token) => MONTH_NAME[token] ?? token),
      band,
    };
  });
}

function gridSentence(year: number, cardId: string, claim: MonthGuideClaim | null): string {
  if (!claim) return GRID_FALLBACKS[cardId] ?? '';
  const templates = GRID_TEMPLATES[cardId] ?? [];
  const template = templates[year % templates.length] ?? templates[0] ?? '';
  return fillTemplate(template, claim);
}

/** The five original cards. The pill names who this stretch is best for. */
export function buildMonthGuide(data: AnnualYearData): MonthGuideCard[] {
  const claims = claimsForYear(data);
  return MONTH_GUIDE_RANGES.map((range, index) => {
    const claim = claims[index] ?? null;
    return {
      ...range,
      tone: monthCardBadge(data, range.months),
      badge: claim ? `Best for ${claim.sites.join(' & ')}` : null,
      claim,
      gridSentence: gridSentence(data.year, range.id, claim),
    };
  });
}

export function summarizeCalendarSeason(data: AnnualYearData): CalendarSeasonSummary {
  const locations = pickLocations(data.locations).map((location) => summarizeLocation(location, data.saturdays));

  const excellentMin = bandMin('Excellent');
  const ranked = data.saturdays.map((label, index) => {
    let goodSites = 0;
    let topRating = 0;
    let excellentSites = 0;
    for (const location of data.locations) {
      const rating = location.cells[index]?.rating ?? 0;
      if (rating > topRating) topRating = rating;
      if (isGoodNight(rating)) goodSites += 1;
      if (rating >= excellentMin) excellentSites += 1;
    }
    return { label, goodSites, topRating, excellentSites, index };
  });

  // Rank by Good-or-better sites, then top rating, then Excellent sites, then earlier Saturday.
  // The list is at least BEST_LIMIT long when that many qualify, and it keeps every later
  // Saturday that ties the cutoff on those three counts. Apr 10 is not dropped for tying Apr 3.
  const sorted = ranked
    .filter((row) => row.goodSites > 0)
    .sort((a, b) => (
      b.goodSites - a.goodSites
      || b.topRating - a.topRating
      || b.excellentSites - a.excellentSites
      || a.index - b.index
    ));
  const cutoff = sorted[Math.min(BEST_LIMIT, sorted.length) - 1];
  const bestSaturdays: BestSaturdayLine[] = (cutoff ? sorted.filter((row, index) => (
    index < BEST_LIMIT
    || (
      row.goodSites === cutoff.goodSites
      && row.topRating === cutoff.topRating
      && row.excellentSites === cutoff.excellentSites
    )
  )) : []).map((row) => ({
      label: row.label,
      goodSites: row.goodSites,
      topRating: row.topRating,
      text: `${row.label}: ${row.goodSites} ${row.goodSites === 1 ? 'site' : 'sites'} at Good or better, top rating ${row.topRating}`,
    }));

  const hasRatings = bestSaturdays.length > 0;
  const faqQuestion = `When is Milky Way season ${data.year}?`;
  const bestClause = joinAnd(
    bestSaturdays.map(
      (row) => `${row.label} (${row.goodSites} ${row.goodSites === 1 ? 'site' : 'sites'}, top rating ${row.topRating})`,
    ),
  );
  const faqAnswer = hasRatings
    ? [
        `In ${data.year}, Good or better means a rating of ${GOOD_NIGHT_MIN} or higher on this grid.`,
        ...locations.map((line) => line.text),
        bestClause ? `The strongest Saturdays on the grid are ${bestClause}.` : '',
        'For exact nights at your own location, use the free planner.',
      ].filter(Boolean).join(' ')
    : `In ${data.year}, this grid has no Saturday rated Good or better, so there is no season window to read off it. For exact nights at your own location, use the free planner.`;

  return {
    hasRatings,
    readingGuide: SEASON_READING_GUIDE,
    locations,
    bestHeading: `Best Saturdays of ${data.year}`,
    bestIntro: 'These are the Saturdays where the most locations on this grid scored Good or better, then the highest top rating, then the most Excellent ratings. A Saturday that ties the last of these on those three counts is included too.',
    bestSaturdays,
    faqQuestion,
    faqAnswer,
  };
}
